import { useContext, useEffect, useState } from 'react';

import withAuth from 'hocs/withAuth/withAuth';
import { calculateRatio } from 'utils/helpers/numbers';

import { SpecialistContext } from 'context/SpecialistContext';
import useOnInit from 'hooks/useOnInit';
import { SpecialistRole } from 'services/specialist';
import {
  getSpecialistDashboardOverview,
  getSpecialistDashboardUsers,
} from 'services/specialist/specialist';
/* import { ClinicalAssessmentResult, LinkReason } from 'services/user'; */

import { TCDashboardTable } from 'components';
import TCDashboardHeader from 'components/TCDashboardHeader';
import { TabData, TabName, TabState } from 'components/TCDashboardTab/types';
import TCDashboardTabs from 'components/TCDashboardTabs';

import { defaultTabsConfig } from './configs';

import { tabNameToUsersFilterName, TCDashboardTableData } from './types';

import * as S from './styles';

const DEFAULT_INITIAL_TAB = TabName.NEW;

const TherapyCompanionDashboard: React.FC = () => {
  const { specialistInfo } = useContext(SpecialistContext);

  const [tabsLoading, setTabsLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(true);
  const [activeUsersAmount, setActiveUsersAmount] = useState(0);
  const [tables, setTables] = useState<TCDashboardTableData[]>([]);
  const [tabsData, setTabsData] = useState<TabData[]>(defaultTabsConfig);
  const [currentTabName, setCurrentTabName] = useState(DEFAULT_INITIAL_TAB);

  const hasGeoPermission =
    typeof specialistInfo?.geoCode !== 'undefined' &&
    specialistInfo.geoCode === 'BR';

  const currentTable = tables.find(({ name }) => name === currentTabName);

  const handleTabSelected = (tabName: TabName) => {
    setCurrentTabName(tabName);
  };

  const loadSpecialistDashboardOverview = async () => {
    const dashboardOverview = await getSpecialistDashboardOverview();

    setActiveUsersAmount(dashboardOverview.active_users_amount);

    let initialTabIsEmpty = false;
    const updatedTabsData = tabsData.map(({ name, type, keyName }) => {
      const tabOverviewCount = dashboardOverview[name];

      if (DEFAULT_INITIAL_TAB === name && !tabOverviewCount) {
        initialTabIsEmpty = true;
      }

      const getState = () => {
        if (tabOverviewCount) {
          if (initialTabIsEmpty) {
            setCurrentTabName(name);
            initialTabIsEmpty = false;
            return TabState.ACTIVE;
          }
          return currentTabName === name ? TabState.ACTIVE : TabState.INACTIVE;
        }
        return TabState.DISABLED;
      };

      return {
        name,
        type,
        keyName,
        state: getState(),
        amount: tabOverviewCount,
      };
    });

    setTabsData(updatedTabsData);
    setTabsLoading(false);
  };

  const loadSpecialistDashboardTable = async (name: TabName) => {
    if (!tableLoading) {
      setTableLoading(true);
    }

    const data = await getSpecialistDashboardUsers(
      tabNameToUsersFilterName(currentTabName),
    );

    const decodedData = data.map(userData => {
      // Custom presorting function to sort extend the sorting to situations where the patients did accomplished any activities.
      // eg.: 0/2 will appear first then 0/3 even if the calculateRatio returns for both 0.
      const doneActivitiesCustomRatio = () => {
        if (
          userData.acknowledged === 0 &&
          userData.total_pages &&
          userData.total_pages > 0
        ) {
          return -userData.total_pages;
        }
        return calculateRatio(userData.acknowledged, userData.total_pages);
      };

      return {
        reason: userData.source_link,
        fullName: userData.full_name,
        linkedAt: userData.created_at,
        phqValue: userData.phq9_value,
        gadValue: userData.gad7_value,
        cognitoId: userData.cognito_id,
        phqResult: userData.phq9_result,
        gadResult: userData.gad7_result,
        unlinkedAt: userData.unlinked_at,
        totalActivities: userData.total_pages,
        doneActivities: userData.acknowledged,
        therapistName: userData.therapist_name,
        dischargeReason: userData.discharge_reason,
        lastMessage: userData.last_message_sent_at,
        lastActivity: userData.last_activity_finished_at,
        doneActivitiesRatio: doneActivitiesCustomRatio(),
      };
    });

    setTables([...tables, { name, data: decodedData }]);
    setTableLoading(false);
  };

  useOnInit(() => {
    loadSpecialistDashboardOverview();
  });

  useEffect(() => {
    if (!currentTable) {
      loadSpecialistDashboardTable(currentTabName);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTabName]);

  return (
    <S.TherapyCompanionDashboardContainer data-testid="therapyCompanionDashboardPage">
      <S.FirstSection>
        <TCDashboardHeader
          specialistName={specialistInfo?.fullName}
          activeUsersTotalAmount={activeUsersAmount}
        />
        <TCDashboardTabs
          onTabSelected={handleTabSelected}
          tabsData={tabsData}
          loading={tabsLoading}
        />
      </S.FirstSection>
      <S.SecondSection>
        <TCDashboardTable
          loading={tableLoading}
          patientsInfo={currentTable?.data}
          filterType={tabNameToUsersFilterName(currentTabName)}
          showActivitiesByGeoCode={hasGeoPermission}
        />
      </S.SecondSection>
    </S.TherapyCompanionDashboardContainer>
  );
};

export default withAuth(TherapyCompanionDashboard, [SpecialistRole.TC]);
